// JAA-326: DOM-marker fallback for Samsung Xclipse GPUs in Chrome, where the
// GLES driver stack corrupts Mapbox symbol quads (skewed streak artifacts).
//
// Strategy: the symbol layers are kept in the style with their icon-opacity
// forced to 0 — Mapbox still places them, so every existing
// queryRenderedFeatures-based click/hover code path keeps working with
// full-size hit boxes. The visuals are mirrored with pointer-events:none DOM
// markers (mapboxgl.Marker), which composite through the regular HTML path
// that is unaffected on these devices (the heart marker and address callout
// already render fine there).
//
// The icon choice logic below mirrors the layer expressions in
// BuildingMap.tsx (sellIconImageExpr / iconSizeExpr / iconOffsetExpr, the
// buy-icon step, the sell tier split) and demandLayers.ts (stageIconExpr,
// coin fade). If those change, keep this file in sync. The stage coins' own
// zoom filter needs no mirror: a coin it hides is not rendered, so it never
// comes back from queryRenderedFeatures.
import mapboxgl from 'mapbox-gl';
import { COIN_SWITCH_ZOOM, stageForWeight } from './demandLayers';

const PUB = process.env.PUBLIC_URL ?? '';

type Kind = 'sell' | 'buy' | 'dot' | 'coin';

// The small dot is a backup for every sell building below
// BUILDING_DOTS_MIN_ZOOM. Full-tier buildings show a pill when it fits;
// otherwise this dot remains visible.
const SELL_DOT_LAYER = 'building-off-market-sell-dot';
const SELL_PILL_LAYER = 'building-off-market-sell-icon';
// Hover/selection overlay. icon-ignore-placement, so it reports every sell
// building regardless of collision — see the presence rule in reconcile.
const SELL_SELECTED_LAYER = 'building-off-market-sell-icon-selected';

// Paired base/-selected layers map to one kind: the hover variant is chosen
// from feature-state instead of rendering two overlapping markers. The sell
// dot is a third variant of that same building marker rather than a kind of
// its own — it shares the @2x pixel ratio, icon-size and icon-offset of the
// pill, so sharing 'sell' also dedupes all three layers onto one marker.
const LAYER_KINDS: Record<string, Kind> = {
  [SELL_PILL_LAYER]: 'sell',
  [SELL_SELECTED_LAYER]: 'sell',
  [SELL_DOT_LAYER]: 'sell',
  'building-off-market-buy-icon': 'buy',
  'building-clickable-dots': 'dot',
  'building-clickable-dots-selected': 'dot',
  'interest-stage-coin': 'coin',
};
const FALLBACK_LAYER_IDS = Object.keys(LAYER_KINDS);

// Marker PNGs are @2x exports (added with pixelRatio 2); the building-dot
// SVGs display at intrinsic size.
const PIXEL_RATIO: Record<Kind, number> = { sell: 2, buy: 2, dot: 1, coin: 2 };

// Display width (CSS px) per icon URL, measured once on first load and shared
// across all maps/markers so subsequent <img> elements can be sized
// synchronously at creation (see makeImg).
const imgBaseWidthByUrl = new Map<string, number>();

// Stacking between marker kinds (all sit above the map canvas).
const Z_INDEX: Record<Kind, string> = { dot: '1', coin: '2', sell: '3', buy: '4' };

type IconLocale = 'en' | 'fi';

interface FeatureProps {
  could_sell_count?: number;
  could_buy_count?: number;
  building_id?: string | number;
  is_new?: boolean;
  is_seen?: boolean;
  w?: number;
}

// --- zoom-dependent mirrors of the layer expressions ---------------------

const lerp = (z: number, z0: number, v0: number, z1: number, v1: number): number =>
  z <= z0 ? v0 : z >= z1 ? v1 : v0 + ((z - z0) / (z1 - z0)) * (v1 - v0);

// iconSizeExpr: zoom 12 -> 0.75, 18 -> 1.0
const iconSizeAt = (z: number): number => lerp(z, 12, 0.75, 18, 1.0);

// iconOffsetExpr: zoom 12 -> [0,0], 15 -> [10*sign, 8], 18 -> [20*sign, 16].
// Mapbox multiplies icon-offset by icon-size for the final pixel offset.
const iconOffsetAt = (z: number, sign: 1 | -1): [number, number] => {
  const x = z <= 15 ? lerp(z, 12, 0, 15, 10) : lerp(z, 15, 10, 18, 20);
  const y = z <= 15 ? lerp(z, 12, 0, 15, 8) : lerp(z, 15, 8, 18, 16);
  const k = iconSizeAt(z);
  return [x * sign * k, y * k];
};

// Buy stacks fade in over [COIN_SWITCH_ZOOM, COIN_SWITCH_ZOOM + 1]; the stage
// coins fade out over [COIN_SWITCH_ZOOM - 1, COIN_SWITCH_ZOOM].
const buyOpacityAt = (z: number): number => lerp(z, COIN_SWITCH_ZOOM, 0, COIN_SWITCH_ZOOM + 1, 1);
const coinOpacityAt = (z: number): number => lerp(z, COIN_SWITCH_ZOOM - 1, 1, COIN_SWITCH_ZOOM, 0);

// --- icon URL resolution (mirrors sellIconImageExpr & friends) ------------

type SellState = 'default' | 'selected' | 'new-default' | 'new-seen' | 'new-selected';

const sellStateOf = (p: FeatureProps, hover: boolean): SellState => {
  if (hover) return p.is_new === true ? 'new-selected' : 'selected';
  if (p.is_new === true && p.is_seen !== true) return 'new-default';
  if (p.is_new === true) return 'new-seen';
  return 'default';
};

const sellIconUrl = (count: number, state: SellState, locale: IconLocale): string | null => {
  const c = Math.max(1, Math.min(9, count));
  const size = c === 1 ? 'single' : `multiple--${c}`;
  const fi = locale === 'fi' ? '--fi' : '';
  switch (state) {
    case 'default':
      return `${PUB}/assets/markers/seller/seller--${size}--default.png`;
    case 'selected':
      return `${PUB}/assets/markers/seller/seller--${size}--selected.png`;
    case 'new-default':
      return `${PUB}/assets/markers/seller-new--${size}--default${fi}.png`;
    case 'new-seen':
      return `${PUB}/assets/markers/seller-new--${size}--seen${fi}.png`;
    case 'new-selected':
      return `${PUB}/assets/markers/seller-new--${size}--selected${fi}.png`;
    default:
      return null;
  }
};

// The dot tier has no count or new-listing variants, and no selected variant
// either: hovering a dot swaps it to the full selected pill via the
// icon-opacity pair on the dot / selected layers.
const SELL_DOT_ICON_URL = `${PUB}/assets/markers/seller/seller--dot--default.png`;

const buyIconUrl = (count: number): string =>
  count >= 10
    ? `${PUB}/assets/markers/buyer--10plus--default.png`
    : `${PUB}/assets/markers/buyer--${Math.max(1, count)}--default.png`;

const dotIconUrl = (hover: boolean): string =>
  `${PUB}/assets/markers/building-dot--single--${hover ? 'selected' : 'default'}.svg`;

// Stage mapping shared with the Mapbox coin layer (demandLayers.stageForWeight).
const coinIconUrl = (weight: number): string =>
  `${PUB}/assets/markers/area-coin--${stageForWeight(weight)}--default.png`;

// --- DOM pill for sell counts >= 10 (mirrors ensureSellMultiIcon) ---------

const SELLER_STATE_STYLES: Record<SellState, { bg: string; border: string; text: string; badge: boolean }> = {
  'default':      { bg: '#FFFFFF', border: '#B3B3B3', text: '#1A1A1A', badge: false },
  'selected':     { bg: '#000000', border: '#000000', text: '#FFFFFF', badge: false },
  'new-default':  { bg: '#FFE033', border: '#AF9A24', text: '#1A1A1A', badge: true },
  'new-seen':     { bg: '#F5F6FA', border: '#B3B3B3', text: '#1A1A1A', badge: true },
  'new-selected': { bg: '#1A1A1A', border: '#1A1A1A', text: '#FFFFFF', badge: true },
};

const buildSellPill = (count: number, state: SellState, locale: IconLocale): HTMLElement => {
  const style = SELLER_STATE_STYLES[state];
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:relative;display:inline-block;';
  const pill = document.createElement('div');
  pill.style.cssText = [
    'display:flex', 'align-items:center', 'gap:4px', 'height:44px',
    'border-radius:22px', 'padding:0 9px', 'box-sizing:border-box',
    `background:${style.bg}`, `border:1px solid ${style.border}`,
  ].join(';');
  const home = document.createElement('img');
  home.src = `${PUB}/assets/markers/seller/Home.png`;
  home.style.cssText = 'width:26px;height:26px;display:block;';
  home.alt = '';
  const num = document.createElement('span');
  num.textContent = String(count);
  num.style.cssText = `font:700 16px Inter,Helvetica,Arial,sans-serif;color:${style.text};min-width:22px;text-align:center;`;
  pill.appendChild(home);
  pill.appendChild(num);
  wrap.appendChild(pill);
  if (style.badge) {
    const badge = document.createElement('span');
    badge.textContent = locale === 'fi' ? 'Uusi' : 'New';
    badge.style.cssText = [
      'position:absolute', 'top:-8px', 'right:-6px', 'width:28px', 'height:14px',
      'background:#FF6666', 'color:#FFFFFF', 'border-radius:4px 4px 4px 0',
      'font:700 9px Silka,Inter,Helvetica,Arial,sans-serif',
      'display:flex', 'align-items:center', 'justify-content:center',
    ].join(';');
    wrap.appendChild(badge);
  }
  return wrap;
};

// --- marker reconciliation -------------------------------------------------

interface MarkerRec {
  marker: mapboxgl.Marker;
  el: HTMLDivElement;
  inner: HTMLElement;
  kind: Kind;
  sig: string;
  // Consecutive reconcile passes where the backing feature was absent from
  // queryRenderedFeatures. Symbol placement is async, so features blink out
  // of query results for a beat while tiles load — only remove a marker
  // after several consecutive misses to avoid teardown/recreate churn.
  misses: number;
}

interface Desired {
  kind: Kind;
  lngLat: [number, number];
  props: FeatureProps;
  hover: boolean;
  // The backup dot renders only when the full pill was not placed.
  dotTier: boolean;
  pillPlaced: boolean;
}

const featureKey = (kind: Kind, f: mapboxgl.MapboxGeoJSONFeature, lngLat: [number, number]): string => {
  const p = (f.properties ?? {}) as FeatureProps;
  const id = f.id ?? p.building_id ?? `${lngLat[0].toFixed(6)},${lngLat[1].toFixed(6)}`;
  return `${kind}:${id}`;
};

export const attachDomMarkerFallback = (
  map: mapboxgl.Map,
  getLocale: () => IconLocale,
): void => {
  const records = new Map<string, MarkerRec>();

  const isLive = (): boolean => {
    try {
      return !!map.getStyle();
    } catch {
      return false;
    }
  };

  // Hide the symbol layers' pixels while keeping them placed and queryable
  // (icon-opacity is paint-time; hit boxes keep their full icon size).
  const forceHideSymbolLayers = (): void => {
    for (const id of FALLBACK_LAYER_IDS) {
      try {
        if (!map.getLayer(id)) continue;
        if (map.getPaintProperty(id, 'icon-opacity') !== 0) {
          map.setPaintProperty(id, 'icon-opacity', 0);
        }
      } catch {
        /* layer race during style reload */
      }
    }
  };

  const applyZoomDependent = (rec: MarkerRec): void => {
    const z = map.getZoom();
    const k = rec.kind === 'sell' || rec.kind === 'buy' ? iconSizeAt(z) : 1;
    rec.inner.style.transform = `scale(${k})`;
    if (rec.kind === 'sell') rec.marker.setOffset(iconOffsetAt(z, 1));
    else if (rec.kind === 'buy') rec.marker.setOffset(iconOffsetAt(z, -1));
    if (rec.kind === 'buy') rec.el.style.opacity = String(buyOpacityAt(z));
    else if (rec.kind === 'coin') rec.el.style.opacity = String(coinOpacityAt(z));
  };

  // Signature of the visual a feature should show — computed WITHOUT building
  // DOM, so the per-second reconcile only touches elements that changed. For
  // image markers the signature is the icon URL itself; runtime pills (sell
  // counts >= 10) get a synthetic `pill:` signature.
  const sigOf = (d: Desired, locale: IconLocale): string | null => {
    const p = d.props;
    if (d.kind === 'sell') {
      if (d.dotTier && !d.hover) return SELL_DOT_ICON_URL;
      const count = Number(p.could_sell_count ?? 0);
      const state = sellStateOf(p, d.hover);
      if (count >= 10) return `pill:${count}:${state}:${locale}`;
      return sellIconUrl(count, state, locale);
    }
    if (d.kind === 'buy') return buyIconUrl(Number(p.could_buy_count ?? 0));
    if (d.kind === 'dot') return dotIconUrl(d.hover);
    return coinIconUrl(Number(p.w ?? 1));
  };

  const buildInnerFromSig = (d: Desired, locale: IconLocale, sig: string): HTMLElement => {
    if (sig.startsWith('pill:')) {
      const count = Number(d.props.could_sell_count ?? 0);
      return buildSellPill(count, sellStateOf(d.props, d.hover), locale);
    }
    return makeImg(sig, d.kind);
  };

  const makeImg = (url: string, kind: Kind): HTMLImageElement => {
    const img = document.createElement('img');
    img.alt = '';
    img.draggable = false;
    img.style.display = 'block';
    img.style.height = 'auto';
    // The PNGs are @2x, so an unsized <img> paints one frame at double size —
    // visible as a "large icon flash" whenever a marker enters the viewport
    // or swaps to its selected variant. Apply the display width synchronously
    // from the per-URL cache; only the very first load of each asset renders
    // hidden for a frame while the width is measured.
    const cached = imgBaseWidthByUrl.get(url);
    if (cached !== undefined) {
      img.style.width = `${cached}px`;
    } else {
      img.style.visibility = 'hidden';
      img.onload = () => {
        const base = (img.naturalWidth || 44) / PIXEL_RATIO[kind];
        imgBaseWidthByUrl.set(url, base);
        img.style.width = `${base}px`;
        img.style.visibility = '';
      };
    }
    img.src = url;
    return img;
  };

  const reconcile = (): void => {
    if (!isLive()) return;

    const present = FALLBACK_LAYER_IDS.filter((id) => {
      try {
        return !!map.getLayer(id);
      } catch {
        return false;
      }
    });
    let feats: mapboxgl.MapboxGeoJSONFeature[] = [];
    if (present.length) {
      try {
        feats = map.queryRenderedFeatures({ layers: present });
      } catch {
        feats = [];
      }
    }

    const locale = getLocale();
    const desired = new Map<string, Desired>();
    for (const f of feats) {
      const layerId = f.layer?.id;
      const kind = layerId ? LAYER_KINDS[layerId] : undefined;
      if (!kind || f.geometry.type !== 'Point') continue;
      const lngLat = f.geometry.coordinates as [number, number];
      const key = featureKey(kind, f, lngLat);
      const hover = (f.state as { hover?: boolean } | undefined)?.hover === true;
      const dotTier = layerId === SELL_DOT_LAYER;
      // The selected overlay is always placed, even for buildings without a
      // visible base marker. Use the base pill or backup dot to establish
      // presence; hovering may then promote either to the selected pill.
      const placed = layerId !== SELL_SELECTED_LAYER || hover;
      const existing = desired.get(key);
      // Paired layers and tile splits yield duplicates; keep one, but let a
      // hovered duplicate win so the selected variant shows.
      if (!existing) {
        if (placed) {
          desired.set(key, {
            kind, lngLat, props: (f.properties ?? {}) as FeatureProps,
            hover, dotTier, pillPlaced: layerId === SELL_PILL_LAYER,
          });
        }
      } else {
        if (hover) existing.hover = true;
        if (layerId === SELL_PILL_LAYER) {
          existing.pillPlaced = true;
          existing.dotTier = false;
        } else if (dotTier && !existing.pillPlaced) {
          existing.dotTier = true;
        }
      }
    }

    // Remove stale markers — but only after several consecutive misses:
    // symbol placement is async, so features transiently vanish from
    // queryRenderedFeatures while tiles load, and immediate teardown would
    // recreate the marker a beat later (visible churn while panning).
    records.forEach((rec, key) => {
      if (desired.has(key)) return;
      rec.misses += 1;
      if (rec.misses >= 3) {
        rec.marker.remove();
        records.delete(key);
      }
    });

    // Add or update.
    desired.forEach((d, key) => {
      const sig = sigOf(d, locale);
      if (!sig) return;
      let rec = records.get(key);
      if (!rec) {
        const inner = buildInnerFromSig(d, locale, sig);
        const el = document.createElement('div');
        el.style.pointerEvents = 'none';
        el.style.zIndex = Z_INDEX[d.kind];
        inner.style.transformOrigin = 'center';
        el.appendChild(inner);
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat(d.lngLat)
          .addTo(map);
        rec = { marker, el, inner, kind: d.kind, sig, misses: 0 };
        records.set(key, rec);
      } else {
        rec.misses = 0;
        rec.marker.setLngLat(d.lngLat);
        if (rec.sig !== sig) {
          const inner = buildInnerFromSig(d, locale, sig);
          inner.style.transformOrigin = 'center';
          rec.el.replaceChild(inner, rec.inner);
          rec.inner = inner;
          rec.sig = sig;
        }
      }
      applyZoomDependent(rec);
    });

    // Hidden only once the DOM replacements exist: if marker construction
    // throws, the symbol layers are still drawing and the map degrades to
    // (corrupted but visible) Mapbox rendering rather than blank markers.
    forceHideSymbolLayers();
  };

  const onZoom = (): void => {
    records.forEach((rec) => applyZoomDependent(rec));
  };

  // Deliberately NOT 'idle'-driven: this app keeps the map render loop busy
  // (animations + repaints), so 'idle' can never fire. Reconcile after camera
  // settles and on data/style churn, debounced, plus a 1 s safety interval
  // that also picks up feature-state (hover/selection) changes.
  let reconcileTimer: number | null = null;
  const scheduleReconcile = (): void => {
    if (reconcileTimer !== null) return;
    reconcileTimer = window.setTimeout(() => {
      reconcileTimer = null;
      reconcile();
    }, 120);
  };
  const interval = window.setInterval(scheduleReconcile, 1000);
  map.on('moveend', scheduleReconcile);
  map.on('zoomend', scheduleReconcile);
  map.on('sourcedata', scheduleReconcile);
  map.on('styledata', () => {
    // A style reload re-adds these layers carrying their original
    // icon-opacity, so re-hide synchronously rather than leaving it to the
    // debounced reconcile — that delay is long enough to render corrupted
    // symbols for a few frames. Re-adding a layer fires styledata itself, so
    // this also catches the restore that BuildingMap defers past the reload;
    // forceHideSymbolLayers skips layers already at 0, so the setPaintProperty
    // it performs cannot feed back into an endless styledata loop.
    forceHideSymbolLayers();
    scheduleReconcile();
  });
  map.on('zoom', onZoom);
  map.on('remove', () => {
    window.clearInterval(interval);
    if (reconcileTimer !== null) window.clearTimeout(reconcileTimer);
    records.forEach((rec) => rec.marker.remove());
    records.clear();
  });
  reconcile();
};
